import React, { useState, useEffect } from 'react';
import { MapPin } from 'lucide-react';
import { formatPublicLocation } from '../../util/listingLocationDisplay';

const SESSION_KEY = 'cys_user_location';

/**
 * Haversine distance between two lat/lng points.
 * Returns distance in miles.
 */
const haversineDistance = (lat1, lng1, lat2, lng2) => {
  const R = 3958.8; // Earth radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/**
 * Format distance for display.
 */
const formatDistance = miles => {
  if (miles < 1) return 'Less than 1 mi away';
  return `${Math.round(miles)} mi away`;
};

/**
 * Read cached user location from sessionStorage only.
 * Never prompts the browser: that happens on the search page.
 */
const getCachedUserLocation = () => {
  try {
    const cached = sessionStorage.getItem(SESSION_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed.lat && parsed.lng) return parsed;
    }
  } catch (e) {
    // sessionStorage unavailable or corrupt
  }
  return null;
};

/**
 * NearbyBadge: shows listing proximity or location.
 *
 * Never prompts for browser geolocation. Only uses cached location
 * from a previous search session (stored in sessionStorage).
 *
 * Priority:
 * 1. If cached user location exists + listing has coords → "X mi away"
 * 2. If listing has address → show address
 * 3. Fallback → "Listed nearby"
 *
 * @param {Object} props
 * @param {Object} [props.geolocation] - { lat, lng } from listing.attributes.geolocation
 * @param {Object} [props.publicData] - listing publicData containing location.address
 * @param {string} [props.className] - optional wrapper class
 */
const NearbyBadge = ({ geolocation, publicData, className }) => {
  const [label, setLabel] = useState(null);

  const address = formatPublicLocation(publicData?.location);

  useEffect(() => {
    if (geolocation?.lat && geolocation?.lng) {
      const userLoc = getCachedUserLocation();
      if (userLoc) {
        const miles = haversineDistance(
          userLoc.lat,
          userLoc.lng,
          geolocation.lat,
          geolocation.lng
        );
        setLabel(formatDistance(miles));
        return;
      }
    }
    // No cached location or no listing coords: show address
    if (address) setLabel(address);
  }, [geolocation?.lat, geolocation?.lng, address]);

  if (!label) return null;

  return (
    <span className={className || 'flex items-center'}>
      <MapPin className="w-4 h-4 mr-1" />
      {label}
    </span>
  );
};

export default NearbyBadge;
